import './HomeTerritoryOptions.styles.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FCX } from '@datassential/platform-ui-lib';
import cn from 'classnames';
import { IoChevronUp } from 'react-icons/io5';
import HomeSectionHeader from '@pages/Home/components/HomeSectionHeader';
import HomeSectionTitle from '@pages/Home/components/HomeSectionTitle';
import HomeSectionLoader from '@pages/Home/components/HomeSectionLoader';
import { useHomeWorkspace } from '@pages/Home/context/HomeWorkspaceContext';
import InputRadio from '@components/new/InputRadio/InputRadio';
import Button from '@components/new/Button/Button';
import NewTooltip from '@components/new/NewTooltip/NewTooltip';
import { TerritoryOption } from '@apiHooks/useTerritoryOptions';
import useIsTruncated from '@hooks/useIsTruncated';
import { useTerritorySelection } from '@pages/Home/context/TerritorySelectionContext';
import { useHomeTerritoryOptions } from '@pages/Home/context/TerritoryOptionsContext';
import { buildSummaryText } from './HomeTerritoryOptions.helpers';
import TerritoryPillGrid from './TerritoryPillGrid';
import { SECTION_ID, TERRITORY_MODES, TERRITORY_MODE_LABELS } from './HomeTerritoryOptions.constants';

interface HomeTerritoryOptionsBodyProps {
    isExpanded: boolean;
    isCompact: boolean;
    states: TerritoryOption[];
    metros: TerritoryOption[];
    isLoading: boolean;
    isApplyDisabled: boolean;
    search: string;
    onSearchChange: (value: string) => void;
    onApply: () => void;
}

const HomeTerritoryOptionsBody: FCX<HomeTerritoryOptionsBodyProps> = ({
    isExpanded,
    isCompact,
    states,
    metros,
    isLoading,
    isApplyDisabled,
    search,
    onSearchChange,
    onApply,
}) => {
    const { mode, selectedIds, setMode, setSelectedIds } = useTerritorySelection();

    const options = mode === 'state' ? states : metros;
    const modeNoun = mode === 'state' ? 'states' : 'metros';
    const selectedCount = selectedIds.length;
    const totalCount = options.length;

    return (
        <>
            {!isCompact && (
                <div className="HomeTerritoryOptions__mode-row">
                    <span className="HomeTerritoryOptions__mode-label">Select Your Territory by:</span>
                    {TERRITORY_MODES.map(m => (
                        <InputRadio
                            key={m}
                            isActive={mode === m}
                            onChange={() => setMode(m)}
                            label={TERRITORY_MODE_LABELS[m]}
                            modifiers={['inherit-label']}
                        />
                    ))}
                </div>
            )}
            <div
                id={`section-${SECTION_ID}-body`}
                className={cn(
                    'HomeTerritoryOptions__collapsible',
                    !isExpanded && 'is-collapsed',
                )}
                aria-hidden={!isExpanded}
            >
                <div className="HomeTerritoryOptions__collapsible-inner">
                    {isLoading ? (
                        <HomeSectionLoader />
                    ) : (
                        <TerritoryPillGrid
                            options={options}
                            selectedIds={selectedIds}
                            onChange={setSelectedIds}
                            search={search}
                            onSearchChange={onSearchChange}
                        />
                    )}
                    <div className="HomeTerritoryOptions__apply-row">
                        <span
                            className={cn(
                                'HomeTerritoryOptions__counter',
                                selectedCount > 0 && 'is-visible',
                            )}
                            aria-live="polite"
                        >
                            {selectedCount > 0 && `${selectedCount} of ${totalCount} ${modeNoun} selected`}
                        </span>
                        <Button
                            isDisabled={isApplyDisabled}
                            className="HomeTerritoryOptions__apply"
                            onClick={onApply}
                        >
                            Apply
                        </Button>
                        <span className="HomeTerritoryOptions__apply-row-spacer" />
                    </div>
                </div>
            </div>
        </>
    );
};

interface TerritorySummaryProps {
    summaryText: string;
    showEdit: boolean;
    onEdit: () => void;
}

const TerritorySummary: FCX<TerritorySummaryProps> = ({
    summaryText,
    showEdit,
    onEdit,
}) => {
    const summaryRef = useRef<HTMLSpanElement | null>(null);
    const isTruncated = useIsTruncated(summaryRef, summaryText);

    return (
        <>
            <div className="HomeTerritoryOptions__summary-wrap">
                <span ref={summaryRef} className="HomeTerritoryOptions__summary">
                    {summaryText}
                </span>
                {isTruncated && (
                    <NewTooltip
                        className="HomeTerritoryOptions__summary-tooltip"
                        position={['left', 'below']}
                    >
                        {summaryText}
                    </NewTooltip>
                )}
            </div>
            {showEdit && (
                <button
                    type="button"
                    className="HomeTerritoryOptions__edit"
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                    }}
                >
                    edit
                </button>
            )}
        </>
    );
};

function sameIdSet(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    return b.every(id => set.has(id));
}

const HomeTerritoryOptions: FCX<{ showInNav?: boolean }> = ({ showInNav = true }) => {
    const { markReady, registerSection, unregisterSection } = useHomeWorkspace();
    const { states, metros, isLoading } = useHomeTerritoryOptions();
    const { mode, selectedIds, savedSelection, saveSelection, resetToSaved } = useTerritorySelection();

    const [isExpanded, setIsExpanded] = useState(!savedSelection);
    const [search, setSearch] = useState('');
    const isCompact = Boolean(savedSelection) && !isExpanded;

    useEffect(() => {
        markReady();
        if (!showInNav) return;
        registerSection(SECTION_ID);
        return () => unregisterSection(SECTION_ID);
    }, [markReady, registerSection, unregisterSection, showInNav]);

    const handleCardClick = useCallback(() => setIsExpanded(true), []);

    const handleApply = useCallback(() => {
        saveSelection(mode, selectedIds);
        setIsExpanded(false);
        setSearch('');
    }, [mode, selectedIds, saveSelection]);

    const handleEditClick = useCallback(() => setIsExpanded(true), []);

    const handleHideMenu = useCallback(() => {
        resetToSaved();
        setIsExpanded(false);
        setSearch('');
    }, [resetToSaved]);

    const summaryText = useMemo(() => {
        if (isLoading) return '';
        if (isExpanded) {
            return buildSummaryText({ mode, ids: selectedIds }, { states, metros });
        }
        return buildSummaryText(savedSelection, { states, metros });
    }, [isLoading, isExpanded, mode, selectedIds, savedSelection, states, metros]);

    const isApplyDisabled = useMemo(() => {
        if (savedSelection === null) {
            return selectedIds.length === 0;
        }
        if (savedSelection.ids.length === 0 && selectedIds.length === 0) {
            return true;
        }
        if (savedSelection.mode !== mode) return false;
        return sameIdSet(savedSelection.ids, selectedIds);
    }, [savedSelection, mode, selectedIds]);

    return (
        <div className="Home__wrapper">
            <div className="Home__sub-wrapper HomeTerritoryOptionsContainer">
                <div
                    className={cn(
                        'HomeTerritoryOptions',
                        !isExpanded && 'is-collapsed',
                    )}
                    onClick={!isExpanded ? handleCardClick : undefined}
                >
                    <HomeSectionHeader className="HomeTerritoryOptions__header">
                        <HomeSectionTitle>My Territory</HomeSectionTitle>
                        {summaryText && (
                            <TerritorySummary
                                summaryText={summaryText}
                                showEdit={Boolean(savedSelection) && !isExpanded && !isLoading}
                                onEdit={handleEditClick}
                            />
                        )}
                    </HomeSectionHeader>
                    <HomeTerritoryOptionsBody
                        isExpanded={isExpanded}
                        isCompact={isCompact}
                        states={states}
                        metros={metros}
                        isLoading={isLoading}
                        isApplyDisabled={isApplyDisabled}
                        search={search}
                        onSearchChange={setSearch}
                        onApply={handleApply}
                    />
                </div>
                <button
                    type="button"
                    className="HomeTerritoryOptions__hide-menu"
                    onClick={isExpanded ? handleHideMenu : handleCardClick}
                    aria-controls={`section-${SECTION_ID}-body`}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'collapse menu' : 'expand menu'}
                >
                    <span className="HomeTerritoryOptions__hide-menu-line" aria-hidden="true"/>
                    {isExpanded && (
                        <span className="HomeTerritoryOptions__hide-menu-toggle">
                            <IoChevronUp size={16} />
                            hide menu
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
};

export default HomeTerritoryOptions;
